import { NavLink, useLocation } from 'react-router-dom';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { AccountMenu } from '@/components/layout/AccountMenu';
import { auth } from '@/api/auth';
import { pageNameForPath } from '@/lib/pageNames';
import { useDynamicPageName } from '@/hooks/useDynamicPageName';
import { useHelpDrawer } from '@/components/help/HelpDrawerContext';
import { useHelpUnreadForPath } from '@/hooks/useHelpUnreadForPath';
import { useSystemHealth } from '@/hooks/useSystemHealth';
import { IconBell, IconHelp } from '@/lib/icons';
import type { User } from '@/types';

interface HeaderProps {
  user: User;
}

export function Header({ user }: HeaderProps) {
  const location = useLocation();
  const dynamicName = useDynamicPageName(location.pathname);
  const pageName = dynamicName ?? pageNameForPath(location.pathname);
  const { openDrawer } = useHelpDrawer();
  const hasUnreadHelp = useHelpUnreadForPath(location.pathname);
  const { alertCount } = useSystemHealth();
  const alertLabel = alertCount >= 100 ? '99+' : String(alertCount);

  const handleLogout = async () => {
    try {
      await auth.logout();
    } finally {
      window.location.href = '/login';
    }
  };

  return (
    <header className="h-14 border-b border-dash-border bg-dash-surface/60 px-6 flex items-center justify-between min-w-0 relative">
      <Breadcrumb pageName={pageName} />
      <div className="flex items-center gap-2 shrink-0">
        <NavLink
          to="/alerts"
          aria-label={
            alertCount > 0
              ? `Alerts (${alertLabel} unread error${alertCount === 1 ? '' : 's'})`
              : 'Alerts'
          }
          className={({ isActive }) =>
            `relative w-8 h-8 rounded-full focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none flex items-center justify-center ${
              isActive
                ? 'text-accent bg-accent/10'
                : 'text-dash-secondary hover:text-dash-primary hover:bg-dash-border/40'
            }`
          }
        >
          <IconBell size={18} aria-hidden="true" />
          {alertCount > 0 ? (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-error text-dash-bg text-[9px] font-mono font-semibold leading-[15px] text-center"
            >
              {alertLabel}
            </span>
          ) : null}
        </NavLink>
        <button
          type="button"
          onClick={() => openDrawer()}
          aria-label="Open help"
          className="relative w-8 h-8 rounded-full text-dash-secondary hover:text-dash-primary hover:bg-dash-border/40 focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none flex items-center justify-center"
        >
          <IconHelp size={18} aria-hidden="true" />
          {hasUnreadHelp ? (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-accent"
            />
          ) : null}
        </button>
        <AccountMenu user={user} onLogout={handleLogout} />
      </div>
      {/* Per-site accent strip. `--site-accent` is set by <SiteLayout> when
          the route is site-scoped; on global routes it resolves to
          transparent so the strip is invisible. */}
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 bottom-0 h-[2px] pointer-events-none"
        style={{ background: 'var(--site-accent, transparent)' }}
      />
    </header>
  );
}
